/**
 * ExamModeSelector Component v1.0
 * 
 * Toggle between CPA and Bar exam modes
 * Updates user preference and AI context
 */

import React, { useState, useCallback } from 'react';
import { GraduationCap, Scale, Check } from 'lucide-react';
import { ExamMode, User } from '../types';
import { useHaptics } from '../hooks/useHaptics';

interface ExamModeSelectorProps {
    user: User | null;
    currentMode: ExamMode;
    onModeChange: (mode: ExamMode) => void;
    compact?: boolean; // For sidebar or header use
    className?: string;
}

interface ExamModeOption {
    id: ExamMode;
    name: string;
    shortName: string;
    description: string;
    icon: React.ReactNode;
    subjects: string[];
    color: string;
    gradient: string;
}

const EXAM_MODES: ExamModeOption[] = [
    {
        id: 'cpa',
        name: 'CPA Board Exam',
        shortName: 'CPA',
        description: 'Philippine CPA Licensure Exam preparation',
        icon: <GraduationCap className="w-5 h-5" />,
        subjects: ['FAR', 'AFAR', 'AUD', 'TAX', 'MAS', 'RFBT'],
        color: '#3b82f6',
        gradient: 'from-blue-500 to-blue-600',
    },
    {
        id: 'bar',
        name: 'Bar Examination',
        shortName: 'BAR',
        description: 'Philippine Bar Exam preparation with IRAC methodology',
        icon: <Scale className="w-5 h-5" />,
        subjects: ['Political', 'Labor', 'Civil', 'Criminal', 'Commercial', 'Tax', 'Ethics', 'Remedial'],
        color: '#8b5cf6',
        gradient: 'from-purple-500 to-purple-600',
    },
];

export const ExamModeSelector: React.FC<ExamModeSelectorProps> = ({
    user,
    currentMode,
    onModeChange,
    compact = false,
    className = '',
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const haptics = useHaptics();

    const selectedMode = EXAM_MODES.find(m => m.id === currentMode) || EXAM_MODES[0];

    const handleModeSelect = useCallback((mode: ExamMode) => {
        haptics.medium();
        onModeChange(mode);
        setIsOpen(false);
    }, [onModeChange, haptics]);

    const handleToggle = useCallback(() => {
        haptics.light();
        setIsOpen(!isOpen);
    }, [isOpen, haptics]);

    // Compact mode - inline segmented toggle
    if (compact) {
        return (
            <div className={`flex rounded-lg p-0.5 bg-zinc-800/80 border border-zinc-700/50 ${className}`}>
                {EXAM_MODES.map((mode) => {
                    const isSelected = mode.id === currentMode;
                    return (
                        <button
                            key={mode.id}
                            onClick={() => handleModeSelect(mode.id)}
                            className={`
                                flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold transition-all duration-200
                                ${isSelected
                                    ? `bg-gradient-to-r ${mode.gradient} text-white shadow-sm`
                                    : 'text-zinc-400 hover:text-zinc-200'
                                }
                            `}
                        >
                            <span className="w-3.5 h-3.5 flex items-center justify-center [&>svg]:w-3.5 [&>svg]:h-3.5">
                                {mode.icon}
                            </span>
                            {mode.shortName}
                        </button>
                    );
                })}
            </div>
        );
    }

    // Full mode - card-based selector
    return (
        <div className={`space-y-3 ${className}`}>
            <h3 className="text-sm font-medium text-zinc-400 mb-2">
                Exam Focus
            </h3>

            <div className="grid grid-cols-2 gap-3">
                {EXAM_MODES.map((mode) => {
                    const isSelected = mode.id === currentMode;

                    return (
                        <button
                            key={mode.id}
                            onClick={() => handleModeSelect(mode.id)}
                            className={`
                relative flex flex-col items-center p-4 rounded-xl
                border-2 transition-all duration-200
                ${isSelected
                                    ? `border-${mode.color} bg-gradient-to-br ${mode.gradient} bg-opacity-10`
                                    : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600'
                                }
              `}
                            style={{
                                borderColor: isSelected ? mode.color : undefined,
                            }}
                        >
                            {/* Selected check */}
                            {isSelected && (
                                <div
                                    className="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center"
                                    style={{ backgroundColor: mode.color }}
                                >
                                    <Check className="w-3 h-3 text-white" />
                                </div>
                            )}

                            {/* Icon */}
                            <div
                                className={`
                  p-3 rounded-xl mb-2
                  ${isSelected ? `bg-gradient-to-br ${mode.gradient}` : 'bg-zinc-700'}
                `}
                            >
                                {mode.icon}
                            </div>

                            {/* Name */}
                            <div className="text-sm font-semibold text-zinc-100">
                                {mode.shortName}
                            </div>

                            {/* Description */}
                            <div className="text-xs text-zinc-400 text-center mt-1">
                                {mode.subjects.slice(0, 3).join(', ')}
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Current mode description */}
            <div className="text-xs text-zinc-500 text-center mt-2">
                {selectedMode.description}
            </div>
        </div>
    );
};

/**
 * Hook for managing exam mode state
 */
export function useExamMode(user: User | null, updateUser?: (updates: User) => void) {
    const [examMode, setExamMode] = useState<ExamMode>(user?.examMode || 'cpa');

    const changeExamMode = useCallback((mode: ExamMode) => {
        setExamMode(mode);

        // Persist to user profile if logged in
        // IMPORTANT: Merge with existing user, don't replace!
        if (user && updateUser) {
            updateUser({ ...user, examMode: mode });
        }

        // Save to local storage as backup
        localStorage.setItem('luca_exam_mode', mode);

        console.log(`[ExamMode] Switched to ${mode.toUpperCase()} mode`);
    }, [user, updateUser]);

    return {
        examMode,
        changeExamMode,
        isBarMode: examMode === 'bar',
        isCpaMode: examMode === 'cpa',
    };
}

export default ExamModeSelector;
